import { z } from "zod";
import { designerTypes, architecteMissions, bureauEtudeMissions, controleurTechniqueMissions, geotechnicienMissions, maitreOeuvreMissions, builderMainActivities } from "@dune/vars";

const participantToSubmissionSocietyFieldsSchema = z.object({
  siren: z.string().optional(),
  denomination: z.string().min(1, "La dénomination est requise"),
  registrationNumber: z.string().optional(),
  country: z.string().optional(),
  streetNumber: z.string().optional(),
  streetName: z.string().optional(),
  postalCode: z.string().optional(),
  city: z.string().optional(),
  totalFeesTTC: z.coerce.number(),
  totalFeesHT: z.coerce.number(),
});

export const designerToSubmissionFormSchema = participantToSubmissionSocietyFieldsSchema.extend({
  designerType: z.enum(designerTypes),
  feeAgreementsFilesIds: z.array(z.string()),
  decennialCertificatesFilesIds: z.array(z.string()).optional(),

  architecteMissions: z.array(z.enum(architecteMissions)).optional(),
  maitreOeuvreMissions: z.array(z.enum(maitreOeuvreMissions)).optional(),
  geotechnicienMissions: z.array(z.enum(geotechnicienMissions)).optional(),
  controleurTechniqueMissions: z.array(z.enum(controleurTechniqueMissions)).optional(),
  bureauEtudeMissions: z.array(z.enum(bureauEtudeMissions)).optional(),
  missions: z.undefined()
}).refine((data) => {
  switch (data.designerType) {
    case "ARCHITECTE":
      return data.architecteMissions && data.architecteMissions.length > 0;
    case "MAITRE_OEUVRE":
      return data.maitreOeuvreMissions && data.maitreOeuvreMissions.length > 0;
    case "GEOTECHNICIEN":
      return data.geotechnicienMissions && data.geotechnicienMissions.length > 0;
    case "CONTROLEUR_TECHNIQUE":
      return data.controleurTechniqueMissions && data.controleurTechniqueMissions.length > 0;
    case "BUREAU_ETUDE":
      return data.bureauEtudeMissions && data.bureauEtudeMissions.length > 0;
    default:
      return false;
  }
}, {
  message: "Sélectionner au moins une mission",
  path: ["missions"]
});

export const builderToSubmissionFormSchema = participantToSubmissionSocietyFieldsSchema.extend({
  quotesFilesIds: z.array(z.string()),
  decennialCertificatesFilesIds: z.array(z.string()).optional(),
  activities: z.array(z.enum(builderMainActivities)).min(1, "Au moins une activité doit être sélectionnée"),
});

export const materialToSubmissionFormSchema = z.object({
  quoteFilesIds: z.array(z.string()),
  materialTypes: z.array(z.string()).min(1, "Au moins un type de matériau doit être spécifié"),
  totalFeesTTC: z.coerce.number().min(0, "Le montant TTC doit être positif"),
  totalFeesHT: z.coerce.number().nullable().optional(),
});

export const participantToSubmissionFormSchema = z.union([
  designerToSubmissionFormSchema,
  builderToSubmissionFormSchema,
  materialToSubmissionFormSchema,
]);

export type DesignerToSubmissionFormData = z.infer<typeof designerToSubmissionFormSchema>;
export type BuilderToSubmissionFormData = z.infer<typeof builderToSubmissionFormSchema>;
export type MaterialToSubmissionFormData = z.infer<typeof materialToSubmissionFormSchema>;
export type ParticipantToSubmissionFormData = z.infer<typeof participantToSubmissionFormSchema>;

export type ParticipantToSubmissionFormMode = "add" | "update";

export const getParticipantToSubmissionType = (data: ParticipantToSubmissionFormData): "DESIGNER" | "BUILDER" | "MATERIAL" => {
  if ("designerType" in data) return "DESIGNER";
  if ("activities" in data) return "BUILDER";
  return "MATERIAL";
}; 